import { useViewState } from '../../lib/useViewState';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { reportError } from '../../lib/errorEvents';
import { melbourneToday } from '../../../shared/dates';
import { SOCIAL_ASSET_CHANNELS, type ProjectWithClient, type SocialAssetWithProject } from '../../../shared/types';
import { PageHeader, SkeletonRows, Tabs } from '../../components/primitives';
import { useIsMobile } from '../../lib/useIsMobile';
import { useQuickAddAction } from '../../lib/quickAdd';
import { assetChannels, calendarMonthWeeks, filtersRevealing, runRepurpose, schedulePatch, socialCounters, type CalendarWeek, type ChannelFilter, type ScheduleDraft, type SocialActions, type SocialFilters } from '../../lib/socialCalendar';
import SocialMobile from '../../components/socialMobile';
import { PageMenu } from '../../components/PageMenu';
import { DotsIcon, PlusIcon, SearchIcon, SocialIcon } from '../../components/icons';
import { type SubView } from './types';
import { SocialStatStrip, SocialEmpty } from './chrome';
import { SocialLibraryBody } from './Library';
import { SocialCalendarBody } from './Calendar';
import { SchedulePopover, SlotActionsPopover, FillWeekPopover } from './Popovers';
import { AssetModal, RestrictModal } from './Modals';
import { socialViewCodec } from './viewState';

export default function Social() {
  const { initial, publish } = useViewState(socialViewCodec, state => {
    setView(state.view);
    setFilters({ status: state.status, brand: state.brand, type: state.type });
    setMonth(state.month);
    setChannel(state.channel);
  });
  const today = melbourneToday();
  const [assets, setAssets] = useState<SocialAssetWithProject[]>([]);
  const [projects, setProjects] = useState<ProjectWithClient[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [view, setView] = useState<SubView>(() => initial.view);
  const [filters, setFilters] = useState<SocialFilters>(() => ({ status: initial.status, brand: initial.brand, type: initial.type }));
  const [openId, setOpenId] = useState<string | null>(null);
  const [defaultMonth] = useState(() => today.slice(0, 7));
  const [selectedMonth, setMonth] = useState(() => initial.month);
  const month = selectedMonth ?? defaultMonth;
  const [channel, setChannel] = useState<ChannelFilter>(() => initial.channel);

  useEffect(() => {
    publish({
      view,
      status: filters.status,
      brand: filters.brand,
      type: filters.type,
      month: selectedMonth,
      channel,
    });
  }, [publish, view, filters.status, filters.brand, filters.type, selectedMonth, channel]);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [fillWeek, setFillWeek] = useState<CalendarWeek | null>(null);
  const [draft, setDraft] = useState<ScheduleDraft | null>(null);
  const [modal, setModal] = useState<{ kind: 'add' } | { kind: 'edit' | 'restrict'; asset: SocialAssetWithProject } | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const isMobile = useIsMobile();
  // A ref, not state: the second click of a double-click lands before any
  // re-render, so only a synchronous flag can turn it away.
  const repurposing = useRef(false);

  // A quick-added asset arrives as `ss:mutated` from the FAB sheet; coalesce
  // those at 250ms (Shell's badge interval) while this page's own mutations
  // still refresh at once and cancel the pending fetch, so a PATCH costs one
  // round trip. The sequence guard drops a response that a newer fetch has
  // already overtaken.
  const listSeq = useRef(0);
  const fetchAssets = useCallback(() => {
    const seq = ++listSeq.current;
    api<{ assets: SocialAssetWithProject[] }>('/api/admin/social-assets')
      .then((d) => { if (seq === listSeq.current) setAssets(d.assets); })
      .catch(() => {})
      .finally(() => { if (seq === listSeq.current) setLoaded(true); });
  }, []);
  const mutatedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refresh = useCallback(() => {
    if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
    mutatedTimer.current = null;
    fetchAssets();
  }, [fetchAssets]);
  useEffect(() => {
    fetchAssets();
    const onMutated = () => {
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      mutatedTimer.current = setTimeout(fetchAssets, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      window.removeEventListener('ss:mutated', onMutated);
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      listSeq.current++;
    };
  }, [fetchAssets]);

  useEffect(() => {
    api<{ projects: ProjectWithClient[] }>('/api/admin/projects')
      .then((d) => setProjects(d.projects))
      .catch(() => {});
  }, []);

  const patchAsset = useCallback(
    async (id: string, json: Record<string, unknown>) => {
      await api(`/api/admin/social-assets/${id}`, { method: 'PATCH', json })
        .catch((error) => reportError(error, 'Could not update the social asset.'));
      refresh();
    },
    [refresh],
  );

  const counters = useMemo(() => socialCounters(assets, today), [assets, today]);
  const ready = useMemo(
    () => assets.filter((a) => a.status === 'ready' && !a.effective_restricted),
    [assets],
  );
  // Two lists, deliberately: the channel chip decides which slots are drawn,
  // never whether a week counts as planned. Only the restriction exclusion is
  // common to both — a blocked asset isn't postable, so it can't hold a week.
  const planned = useMemo(
    () => assets.filter((a) => a.planned_date !== null && !a.effective_restricted),
    [assets],
  );
  const plannedInChannel = useMemo(
    () =>
      channel === 'all'
        ? planned
        : planned.filter((a) => assetChannels(a.channels).includes(channel)),
    [planned, channel],
  );
  const weeks = useMemo(
    () => calendarMonthWeeks(month, plannedInChannel, today, planned),
    [month, plannedInChannel, planned, today],
  );

  const startSchedule = (asset: SocialAssetWithProject, date?: string) => {
    // Belt and braces with the popover's own guard: a used asset never
    // re-enters the plan (that would strand used_at against a live slot).
    if (asset.status === 'used') return;
    setSelectedSlotId(null);
    setFillWeek(null);
    // Both channels pre-selected, matching the approved popover; either chip
    // deselects back to a single-channel slot.
    setDraft({
      asset,
      date: date ?? asset.planned_date ?? today,
      channels: assetChannels(asset.channels).length > 0
        ? assetChannels(asset.channels)
        : [...SOCIAL_ASSET_CHANNELS],
    });
  };

  /** Keep a just-created or just-saved asset on screen: relax whichever
      filter chip hides it and open its drawer, in the library — the calendar
      can only show an asset once it has a date, so a needs-prep copy made
      from a used slot has to land over there to be seen at all. */
  const revealAsset = (saved: SocialAssetWithProject) => {
    setSelectedSlotId(null);
    setFillWeek(null);
    setDraft(null);
    setView('library');
    setFilters((current) => filtersRevealing(saved, current));
    setOpenId(saved.id);
  };

  const actions: SocialActions = {
    onAdd: () => setModal({ kind: 'add' }),
    onOpenAsset: (id) => setOpenId((current) => (current === id ? null : id)),
    onEdit: (asset) => setModal({ kind: 'edit', asset }),
    onDelete: async (asset) => {
      await api(`/api/admin/social-assets/${asset.id}`, { method: 'DELETE' })
        .catch((error) => reportError(error, 'Could not delete the social asset.'));
      setOpenId(null);
      refresh();
    },
    onRestrict: (asset) => setModal({ kind: 'restrict', asset }),
    onUnrestrict: (asset) => patchAsset(asset.id, { unrestrict: true }),
    onMarkReady: (asset) => {
      if (asset.status === 'used') return;
      return patchAsset(asset.id, { status: 'ready' });
    },
    onRepurpose: async (asset) => {
      const created = await runRepurpose(asset, repurposing, (json) =>
        api<{ asset: SocialAssetWithProject }>('/api/admin/social-assets', {
          method: 'POST',
          json,
        }),
      );
      // Blocked as a double-click, or the POST failed: nothing was created,
      // so there is nothing to refresh or reveal.
      if (created === null) return;
      revealAsset(created);
      refresh();
    },
    onStartSchedule: startSchedule,
    onUnschedule: (asset) => {
      if (asset.status === 'used') return;
      setSelectedSlotId(null);
      return patchAsset(asset.id, { status: 'ready', planned_date: null, channels: null });
    },
    onMarkUsed: (asset) => {
      if (asset.status === 'used') return;
      setSelectedSlotId(null);
      return patchAsset(asset.id, { mark_used: true });
    },
  };

  // The FAB's first row while this page is mounted (§0.4.8) — the same
  // "Add asset" form the page menu and the desktop chip open.
  useQuickAddAction({ label: 'New asset', icon: <SocialIcon />, onSelect: actions.onAdd });

  const onAssetSaved = (saved: SocialAssetWithProject) => {
    revealAsset(saved);
    refresh();
  };

  const confirmSchedule = async () => {
    if (draft === null) return;
    const { asset } = draft;
    setDraft(null);
    await patchAsset(asset.id, schedulePatch(draft));
  };

  const schedulePopover = draft !== null && (
    <SchedulePopover
      draft={draft}
      onChange={setDraft}
      onCancel={() => setDraft(null)}
      onConfirm={confirmSchedule}
    />
  );

  const selectedSlot = selectedSlotId !== null
    ? assets.find((a) => a.id === selectedSlotId) ?? null
    : null;

  const calendarPopover = schedulePopover || (
    fillWeek !== null ? (
      <FillWeekPopover
        week={fillWeek}
        ready={ready}
        onPick={(asset) => {
          const date = fillWeek.start >= today ? fillWeek.start : today;
          setFillWeek(null);
          startSchedule(asset, date);
        }}
        onClose={() => setFillWeek(null)}
      />
    ) : selectedSlot !== null ? (
      <SlotActionsPopover
        asset={selectedSlot}
        actions={actions}
        onClose={() => setSelectedSlotId(null)}
      />
    ) : null
  );

  return (
    <>
      <PageHeader
        title="Social"
        count={assets.length}
        mobileActions={isMobile ? [
          {
            key: 'search',
            label: 'Search',
            icon: <SearchIcon />,
            onPress: () => window.dispatchEvent(new Event('ss:open-search')),
          },
          {
            key: 'menu',
            label: 'More social actions',
            icon: <DotsIcon />,
            onPress: () => setShowMenu(true),
          },
        ] : undefined}
      >
        {!isMobile && <span className="social-sub">content library &amp; calendar</span>}
      </PageHeader>

      {isMobile ? (
        <SocialMobile
          assets={assets}
          loaded={loaded}
          today={today}
          counters={counters}
          view={view}
          onView={setView}
          filters={filters}
          onFilter={setFilters}
          openId={openId}
          month={month}
          onMonth={setMonth}
          channel={channel}
          onChannel={setChannel}
          weeks={weeks}
          ready={ready}
          actions={actions}
          onSchedule={(next) => patchAsset(next.asset.id, schedulePatch(next))}
          empty={<SocialEmpty view={view} onAdd={actions.onAdd} />}
        />
      ) : (
      <>
      <div className="toolbar">
        <Tabs
          options={[
            { value: 'calendar' as SubView, label: 'Calendar' },
            { value: 'library' as SubView, label: 'Library' },
          ]}
          value={view}
          onChange={setView}
        />
      </div>

      <div className="page-body">
        <div className="content wide">
          <SocialStatStrip counters={counters} />

          {!loaded ? (
            <SkeletonRows rows={6} />
          ) : assets.length === 0 ? (
            <SocialEmpty view={view} onAdd={actions.onAdd} />
          ) : view === 'library' ? (
            <SocialLibraryBody
              assets={assets}
              filters={filters}
              onFilter={setFilters}
              openId={openId}
              actions={actions}
              popover={schedulePopover}
            />
          ) : (
            <SocialCalendarBody
              month={month}
              weeks={weeks}
              channel={channel}
              onMonth={setMonth}
              onChannel={setChannel}
              ready={ready}
              today={today}
              selectedSlotId={selectedSlotId}
              onSelectSlot={setSelectedSlotId}
              onFillWeek={(week) => {
                setDraft(null);
                setSelectedSlotId(null);
                setFillWeek(week);
              }}
              actions={actions}
              popover={calendarPopover}
            />
          )}
        </div>
      </div>
      </>
      )}

      {isMobile && (
        <PageMenu
          open={showMenu}
          onClose={() => setShowMenu(false)}
          items={[{ key: 'new', icon: <PlusIcon />, label: 'New asset', onSelect: actions.onAdd }]}
        />
      )}

      {modal?.kind === 'add' && (
        <AssetModal
          asset={null}
          projects={projects}
          onClose={() => setModal(null)}
          onSaved={onAssetSaved}
        />
      )}
      {modal?.kind === 'edit' && (
        <AssetModal
          asset={modal.asset}
          projects={projects}
          onClose={() => setModal(null)}
          onSaved={onAssetSaved}
        />
      )}
      {modal?.kind === 'restrict' && (
        <RestrictModal
          asset={modal.asset}
          onClose={() => setModal(null)}
          onConfirm={(restricted_note) => {
            setModal(null);
            void patchAsset(modal.asset.id, { restrict: true, restricted_note });
          }}
        />
      )}
    </>
  );
}
